/**
 * ============================================================================
 * Reference Table Editor V2 - With Infinite Scroll, Search, Filter & Sort
 * ============================================================================
 * 
 * Composant amélioré pour l'édition des tables de référence avec :
 * - Scroll infini (virtualisation)
 * - Recherche avancée multi-colonnes
 * - Filtrage par colonne
 * - Tri ascendant/descendant
 * 
 * ============================================================================
 */

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Search,
  Filter,
  ArrowUpDown,
  ChevronDown,
  X,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import ConfirmationDialog from "./ConfirmationDialog";

// ============================================================================
// TYPES
// ============================================================================

export interface ColumnConfig {
  key: string;
  label: string;
  type: "text" | "number" | "select" | "textarea" | "date";
  editable: boolean;
  width?: string;
  sortable?: boolean;
  filterable?: boolean;
}

interface FilterState {
  column: string;
  operator: "equals" | "contains" | "startsWith" | "endsWith" | "gt" | "lt";
  value: string;
}

interface SortState {
  column: string;
  direction: "asc" | "desc";
}

// ============================================================================
// COMPONENT
// ============================================================================

interface ReferenceTableEditorV2Props {
  tableId: "fe_master" | "mapping" | "jobs" | "geo_units";
  title: string;
  description?: string;
  columns: ColumnConfig[];
  onSave: (rowId: string | number, columnName: string, newValue: any) => Promise<void>;
  pageSize?: number;
  searchable?: boolean;
}

export default function ReferenceTableEditorV2({
  tableId,
  title,
  description,
  columns,
  onSave,
  pageSize = 50,
  searchable = true,
}: ReferenceTableEditorV2Props) {
  // State
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterState[]>([]);
  const [sort, setSort] = useState<SortState | null>(null);
  const [editingCell, setEditingCell] = useState<{
    rowId: string | number;
    columnKey: string;
    value: any;
  } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    rowId?: string | number;
    columnKey?: string;
    newValue?: any;
  }>({ isOpen: false });
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    new Set(columns.map((c) => c.key))
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Query pour récupérer les données
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    trpc.upload.getTableData.useInfiniteQuery(
      {
        tableId,
        pageSize,
        search: searchQuery || undefined,
      },
      {
        getNextPageParam: (lastPage) =>
          lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
      }
    );

  // Réinitialiser le scroll quand la recherche change
  useEffect(() => {
    if (searchQuery) {
      // Réinitialiser les données quand on fait une nouvelle recherche
      setPage(1);
    }
  }, [searchQuery]);

  // Mutation pour mettre à jour une cellule
  const updateCellMutation = trpc.upload.updateCell.useMutation({
    onSuccess: () => {
      toast.success("Cellule mise à jour avec succès");
      setConfirmDialog({ isOpen: false });
      setEditingCell(null);
    },
    onError: (error) => {
      toast.error(`Erreur: ${error.message}`);
    },
  });

  // Aplatir les données paginées
  const allRows = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  // Appliquer les filtres et le tri
  const filteredAndSortedRows = useMemo(() => {
    let rows = [...allRows];

    // Appliquer les filtres
    filters.forEach((filter) => {
      rows = rows.filter((row) => {
        const value = String(row[filter.column] || "").toLowerCase();
        const filterValue = filter.value.toLowerCase();

        switch (filter.operator) {
          case "equals":
            return value === filterValue;
          case "contains":
            return value.includes(filterValue);
          case "startsWith":
            return value.startsWith(filterValue);
          case "endsWith":
            return value.endsWith(filterValue);
          case "gt":
            return Number(row[filter.column]) > Number(filter.value);
          case "lt":
            return Number(row[filter.column]) < Number(filter.value);
          default:
            return true;
        }
      });
    });

    // Appliquer le tri
    if (sort) {
      rows.sort((a, b) => {
        const aVal = a[sort.column];
        const bVal = b[sort.column];

        if (typeof aVal === "number" && typeof bVal === "number") {
          return sort.direction === "asc" ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal || "").toLowerCase();
        const bStr = String(bVal || "").toLowerCase();

        if (sort.direction === "asc") {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }

    return rows;
  }, [allRows, filters, sort]);

  // Calculer le nombre total de résultats
  const totalResults = data?.pages[0]?.total || 0;
  const totalPages = data?.pages[0]?.totalPages || 0;

  // Gestion du scroll infini
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 500;

      if (isNearBottom && hasNextPage && !isFetchingNextPage && !isLoadingMore) {
        setIsLoadingMore(true);
        fetchNextPage().finally(() => setIsLoadingMore(false));
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage, isLoadingMore, fetchNextPage]);

  // Handlers
  const handleAddFilter = (column: string) => {
    setFilters([
      ...filters,
      { column, operator: "contains", value: "" },
    ]);
    // Réinitialiser la pagination quand on ajoute un filtre
    setPage(1);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
    // Réinitialiser la pagination quand on supprime un filtre
    setPage(1);
  };

  const handleUpdateFilter = (
    index: number,
    updates: Partial<FilterState>
  ) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], ...updates };
    setFilters(newFilters);
    // Réinitialiser la pagination quand on met à jour un filtre
    setPage(1);
  };

  const handleSort = (columnKey: string) => {
    if (sort?.column === columnKey) {
      setSort({
        column: columnKey,
        direction: sort.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSort({ column: columnKey, direction: "asc" });
    }
    // Réinitialiser la pagination quand on change le tri
    setPage(1);
  };

  const handleEditCell = (
    rowId: string | number,
    columnKey: string,
    currentValue: any
  ) => {
    setEditingCell({ rowId, columnKey, value: currentValue });
  };

  const handleSaveCell = async (newValue: any) => {
    if (!editingCell) return;

    setConfirmDialog({
      isOpen: true,
      rowId: editingCell.rowId,
      columnKey: editingCell.columnKey,
      newValue,
    });
  };

  const handleConfirmSave = async () => {
    if (!confirmDialog.rowId || !confirmDialog.columnKey) return;

    try {
      await updateCellMutation.mutateAsync({
        tableId,
        rowId: confirmDialog.rowId,
        columnName: confirmDialog.columnKey,
        newValue: confirmDialog.newValue,
      });
    } catch (error) {
      console.error("Error saving cell:", error);
    }
  };

  const handleToggleColumn = (columnKey: string) => {
    const newVisibleColumns = new Set(visibleColumns);
    if (newVisibleColumns.has(columnKey)) {
      newVisibleColumns.delete(columnKey);
    } else {
      newVisibleColumns.add(columnKey);
    }
    setVisibleColumns(newVisibleColumns);
  };

  // Colonnes visibles
  const visibleColumnsArray = columns.filter((c) =>
    visibleColumns.has(c.key)
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Search */}
        {searchable && (
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-8"
              />
            </div>
          </div>
        )}

        {/* Filter Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filtrer {filters.length > 0 && `(${filters.length})`}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {filters.map((filter, index) => (
              <div key={index} className="p-2 space-y-2 border-b">
                <div className="flex items-center justify-between">
                  <select
                    value={filter.column}
                    onChange={(e) =>
                      handleUpdateFilter(index, { column: e.target.value })
                    }
                    className="text-xs border rounded px-2 py-1 flex-1"
                  >
                    {columns.map((col) => (
                      <option key={col.key} value={col.key}>
                        {col.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveFilter(index)}
                    className="ml-2"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex gap-1">
                  <select
                    value={filter.operator}
                    onChange={(e) =>
                      handleUpdateFilter(index, {
                        operator: e.target.value as FilterState["operator"],
                      })
                    }
                    className="text-xs border rounded px-2 py-1 flex-1"
                  >
                    <option value="contains">Contient</option>
                    <option value="equals">Égal</option>
                    <option value="startsWith">Commence par</option>
                    <option value="endsWith">Finit par</option>
                    <option value="gt">Plus grand que</option>
                    <option value="lt">Plus petit que</option>
                  </select>
                </div>
                <Input
                  placeholder="Valeur"
                  value={filter.value}
                  onChange={(e) =>
                    handleUpdateFilter(index, { value: e.target.value })
                  }
                  className="h-8 text-xs"
                />
              </div>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleAddFilter(columns[0].key)}
              className="cursor-pointer"
            >
              + Ajouter un filtre
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Column Visibility Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <ChevronDown className="h-4 w-4 mr-2" />
              Colonnes
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {columns.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.key}
                checked={visibleColumns.has(col.key)}
                onCheckedChange={() => handleToggleColumn(col.key)}
              >
                {col.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Clear Filters */}
        {filters.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters([])}
          >
            Effacer les filtres
          </Button>
        )}
      </div>

      {/* Table Container with Infinite Scroll */}
      <Card className="overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto overflow-y-auto max-h-[600px]"
        >
          <table className="w-full text-sm">
            {/* Header */}
            <thead className="bg-muted sticky top-0 z-10">
              <tr>
                {visibleColumnsArray.map((col) => (
                  <th
                    key={col.key}
                    className={`px-4 py-2 text-left font-semibold border-b ${
                      col.width || "w-32"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{col.label}</span>
                      {col.sortable !== false && (
                        <button
                          onClick={() => handleSort(col.key)}
                          className="hover:bg-background rounded p-1"
                        >
                          <ArrowUpDown
                            className={`h-3 w-3 ${
                              sort?.column === col.key
                                ? "text-primary"
                                : "text-muted-foreground"
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Body */}
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={visibleColumnsArray.length}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : filteredAndSortedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumnsArray.length}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Aucun enregistrement trouvé
                  </td>
                </tr>
              ) : (
                filteredAndSortedRows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="border-b hover:bg-muted/50 transition-colors"
                  >
                    {visibleColumnsArray.map((col) => {
                      const isEditing =
                        editingCell?.rowId === row.id &&
                        editingCell?.columnKey === col.key;
                      const value = row[col.key];

                      return (
                        <td
                          key={col.key}
                          className={`px-4 py-2 ${
                            col.editable ? "cursor-pointer hover:bg-primary/10" : ""
                          }`}
                          onClick={() => {
                            if (col.editable && !isEditing) {
                              handleEditCell(row.id, col.key, value);
                            }
                          }}
                        >
                          {isEditing ? (
                            <div className="flex gap-1">
                              {col.type === "textarea" ? (
                                <textarea
                                  value={editingCell.value}
                                  onChange={(e) =>
                                    setEditingCell({
                                      ...editingCell,
                                      value: e.target.value,
                                    })
                                  }
                                  className="border rounded px-2 py-1 text-xs flex-1 h-20"
                                  autoFocus
                                />
                              ) : (
                                <input
                                  type={col.type === "number" ? "number" : "text"}
                                  value={editingCell.value}
                                  onChange={(e) =>
                                    setEditingCell({
                                      ...editingCell,
                                      value:
                                        col.type === "number"
                                          ? Number(e.target.value)
                                          : e.target.value,
                                    })
                                  }
                                  className="border rounded px-2 py-1 text-xs flex-1"
                                  autoFocus
                                />
                              )}
                              <button
                                onClick={() =>
                                  handleSaveCell(editingCell.value)
                                }
                                className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => setEditingCell(null)}
                                className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-400"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="truncate">
                                {value !== null && value !== undefined
                                  ? String(value)
                                  : "-"}
                              </span>
                              {col.editable && (
                                <span className="text-xs text-muted-foreground">
                                  ✎
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Loading More Indicator */}
        {isFetchingNextPage && (
          <div className="px-4 py-4 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
            Chargement de plus de données...
          </div>
        )}
      </Card>

      {/* Info */}
      <div className="text-xs text-muted-foreground space-y-1">
        <div>
          Affichés: {filteredAndSortedRows.length} enregistrement
          {filteredAndSortedRows.length > 1 ? "s" : ""}
        </div>
        {searchQuery && (
          <div>
            Résultats de recherche: {totalResults} enregistrement
            {totalResults > 1 ? "s" : ""} trouvé{totalResults > 1 ? "s" : ""}
          </div>
        )}
        {!searchQuery && (
          <div>
            Total dans la base: {totalResults} enregistrement
            {totalResults > 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title="Confirmer la modification"
        description="Es-tu sûr de vouloir confirmer cette modification ?"
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmDialog({ isOpen: false })}
        isLoading={updateCellMutation.isPending}
      />
    </div>
  );
}
